<template>
	<div class="mb-4 stats-container">
		<div
			class="media-info grid w-full bg-surface-2 rounded-xl overflow-hidden shadow-[0_2px_6px_rgba(0,0,0,0.28)] ring-1 ring-inset ring-white/[0.04] mt-1 md:mt-4">
			<!-- Viewers -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Viewers</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="streamAlive">
						<span v-if="clientsCount !== -1">{{ numberFormat(clientsCount) }}</span>
						<span v-else
							class="inline-block size-3 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
					</template>
					<span v-else class="text-surface-6" title="Waiting for stream data">—</span>
				</div>
			</div>
			<!-- Bitrate (video primary, audio secondary) -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Bitrate</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="streamAlive">
						<span v-if="mediaPulse.bitrate">
							{{ mediaPulse.bitrate }}
							<span class="text-xs leading-4 text-surface-7">kbps</span>
						</span>
						<span v-else
							class="inline-block size-3 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
					</template>
					<span v-else class="text-surface-6" title="Waiting for stream data">—</span>
				</div>
				<div class="mt-1 text-xs leading-4 text-surface-7 whitespace-nowrap">
					Audio
					<!-- pulse carries a single audioBitrate (last track), so with
					     multiple audio tracks show the count instead of one
					     unrepresentative bitrate -->
					<template v-if="streamAlive && audioTrackCount > 1">
						<span class="text-surface-8 tabular-nums">{{ audioTrackCount }}</span> tracks
					</template>
					<template v-else-if="streamAlive && mediaPulse.audioBitrate">
						<span class="text-surface-8 tabular-nums">{{ mediaPulse.audioBitrate }}</span> kbps
					</template>
					<span v-else class="text-surface-6">—</span>
				</div>
			</div>
			<!-- Quality: resolution, fps & codecs combined -->
			<div class="stat-quality flex flex-col items-start justify-start px-3 py-3 text-left">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Format</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="streamAlive && streamResolution">
						{{ streamResolution }}
						<span v-if="mediaPulse.fps" class="text-xs leading-4 text-surface-7">{{ normalizedFPS(mediaPulse.fps) }} FPS</span>
					</template>
					<span v-else class="text-surface-6" title="Waiting for stream data">—</span>
				</div>
				<div v-if="streamAlive && codecBadges.visible.length"
					class="mt-1 flex gap-x-1 whitespace-nowrap">
					<Badge v-for="(group, index) in codecBadges.visible" :key="index" inline size="xs" class="uppercase"
						:type="group.type">{{ group.codec }}<template v-if="group.count > 1"> ×{{ group.count }}</template></Badge>
					<Tooltip v-if="codecBadges.overflow.length" :content="codecOverflowTitle">
						<Badge inline size="xs" type="main">+{{ codecBadges.overflow.length }}</Badge>
					</Tooltip>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import StreamService from '@/services/StreamService';
import MetricsService from '@/services/MetricsService';
import { bytes, numberFormat, normalizedFPS } from '@/filtersNew';
import Badge from '../Badge.vue';
import Tooltip from '../Atoms/Tooltip.vue';

export default {
	name: 'SubSecondStats',
	components: {
		Badge,
		Tooltip
	},
	props: {
		stream: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id');
			}
		},
		videoFiles: {
			type: Array,
			default() { return []; }
		},
		mediaPulse: {
			type: Object,
			default() { return {}; }
		}
	},
	data() {
		return {
			scopeAlive: true,
			processing: true,
			processingMessage: null,
			windowHeight: 0,
			clientsCount: -1,
			totalEncodingStorageTime: 0,
			bandwidthStats: {
				value: 0,
				unit: 'bytes',
				loaded: false
			},
			encodingStats: {
				value: 0,
				loaded: false
			},
			storageStats: {
				value: 0,
				unit: 'bytes',
				loaded: false
			},
			columns: [
				{ id: 'stream_status', streamTypes: ['restream', 'scheduled'] },
				{ id: 'analytics', streamTypes: ['live', 'ipcam', 'vod'] },
				{
					id: 'stream_bitrate',
					streamTypes: ['restream', 'live', 'ipcam', 'scheduled']
				},
				{ id: 'media_quality', streamTypes: ['restream', 'live', 'ipcam'] },
				{ id: 'media_codecs', streamTypes: ['restream', 'live', 'ipcam'] },
				{ id: 'videos_count', streamTypes: ['scheduled', 'vod'] },
				{ id: 'bytes_storage', streamTypes: ['scheduled', 'vod'] }
			]
		};
	},
	computed: {
		subscription() {
			let subs = this.$store.state.User.subscriptions.find(sub => sub.category && sub.category.includes('live'));
			return subs;
		},
		streamAlive() {
			return this.stream.enabled && this.mediaPulse && this.mediaPulse.alive;
		},
		streamResolution() {
			return (this.streamAlive && this.mediaPulse.width) ? `${this.mediaPulse.width} × ${this.mediaPulse.height}` : null;
		},
		audioTrackCount() {
			const codecs = (this.mediaPulse && this.mediaPulse.codecs) || [];
			return codecs.filter(track => track && track.type === 'audio').length;
		},
		// pulse.codecs has one entry per track, so multi-language streams
		// (e.g. 10 AAC audio tracks) are deduped into "AAC ×10". The visible
		// set is hard-capped so the nowrap badge row can never clip, even in
		// the narrow 2×2 layout; the remainder collapses into a "+N" badge.
		codecBadges() {
			const codecs = (this.mediaPulse && this.mediaPulse.codecs) || [];
			const groups = [];
			codecs.forEach((track) => {
				if (!track || !track.codec) return;
				const type = track.type ?? 'main';
				const found = groups.find(g => g.type === type && g.codec === track.codec);
				if (found) found.count += 1;
				else groups.push({ type, codec: track.codec, count: 1 });
			});
			const MAX_VISIBLE = 2;
			if (groups.length <= MAX_VISIBLE) return { visible: groups, overflow: [] };
			return { visible: groups.slice(0, MAX_VISIBLE), overflow: groups.slice(MAX_VISIBLE) };
		},
		codecOverflowTitle() {
			return this.codecBadges.overflow
				.map(g => `${String(g.codec).toUpperCase()}${g.count > 1 ? ` ×${g.count}` : ''}`)
				.join(', ');
		}
	},
	mounted() {
		this.setupBandwidthCounter();
		this.setupViewershipCounter();
	},
	unmounted() {
		this.scopeAlive = false;
	},
	methods: {
		numberFormat,
		normalizedFPS,
		onClickBitrate() {
			if (this.stream.type === 'live' || this.stream.type === 'restream') this.$router.push({ name: 'StreamsManageAnalyticsInputHealthCharts' })
		},
		setupViewershipCounter() {
			(async function updateCounter() {
				let timeoutInterval = 5000;
				let shouldUpdateValue = this.stream.enabled;
				if (document.hidden) {
					shouldUpdateValue = false;
				}

				if (shouldUpdateValue) {
					if (!document.hasFocus()) {
						timeoutInterval *= 6;
					}
				}

				if (shouldUpdateValue) {
					try {
						const data = await StreamService.getSubSecondStreamViewers(
							this.stream.key
						);
						this.clientsCount = data.viewers || 0;
					} catch (e) { }
				}

				if (this.scopeAlive) {
					setTimeout(updateCounter.bind(this), timeoutInterval);
				}
			}.bind(this)());
		},
		setupBandwidthCounter() {
			(async function updateCounter() {
				let timeoutInterval = 10000;
				let shouldUpdateValue =
					this.stream.enabled || !this.bandwidthStats.loaded;
				if (document.hidden) {
					shouldUpdateValue = false;
				}

				if (shouldUpdateValue) {
					if (!document.hasFocus()) {
						timeoutInterval *= 6;
					}
				}

				if (shouldUpdateValue) {
					try {
						let startTime = this.subscription.cstart;
						if (_.isString(startTime)) {
							startTime = new Date(startTime).getTime();
							if (this.subscription.enabled === false) {
								startTime = moment(startTime).utc().startOf('day').valueOf()
							}
						}
						const bandwidthRes = await MetricsService.getStreamBandwidth(
							this.stream._id,
							startTime
						);
						// }
						const downloadedBytes = _.get(bandwidthRes, 'bytes') || 0;
						const bytesInfo = bytes(
							downloadedBytes >= 100000000 ? downloadedBytes : 0, // only show bandwidth > 100MB
							true,
							2,
							true
						);
						this.bandwidthStats = _.assign(
							{},
							this.bandwidthStats,
							bytesInfo
						);
						this.bandwidthStats.loaded = true;
					} catch (e) {
						console.log(e);
					}
				}

				if (this.scopeAlive) {
					setTimeout(updateCounter.bind(this), timeoutInterval);
				}
			}.bind(this)());
		},
		setupStorageCounter() {
			(async function updateCounter() {
				let timeoutInterval = 10000;
				let shouldUpdateValue = true;
				if (document.hidden) {
					shouldUpdateValue = false;
				}

				if (shouldUpdateValue) {
					if (!document.hasFocus()) {
						timeoutInterval *= 6;
					}
				}

				if (shouldUpdateValue) {
					try {
						const productId = this.subscription.package._id;
						const storageRes = await MetricsService.getSubscriptionStorage(
							undefined,
							productId
						);

						const bytesConsumed = _.get(storageRes, 'bytes') || 0;
						const bytesInfo = bytes(
							bytesConsumed,
							true,
							2,
							true
						);

						this.storageStats = _.assign(
							{},
							this.storageStats,
							bytesInfo
						);
						this.storageStats.loaded = true;
					} catch (e) { }
				}

				if (this.scopeAlive) {
					setTimeout(updateCounter.bind(this), timeoutInterval);
				}
			}.bind(this)());
		},
		setupEncodingCounter() {
			(async function updateCounter() {
				let timeoutInterval = 30000;
				// let shouldUpdateValue = !this.encodingStats.loaded
				let shouldUpdateValue = true;
				if (document.hidden) {
					shouldUpdateValue = false;
				}

				if (shouldUpdateValue) {
					if (!document.hasFocus()) {
						timeoutInterval *= 6;
					}
				}

				if (shouldUpdateValue) {
					try {
						const productId = this.subscription.package._id;
						const encodingUsageInfo = await MetricsService.getSubscriptionEncodingUnits(null, productId);
						const minsEncoded = _.get(encodingUsageInfo, 'units') || 0;
						this.encodingStats = {
							value: minsEncoded > 0 ? Math.round(minsEncoded) : minsEncoded,
							loaded: true
						};
					} catch (e) {
						console.log('e', e);
					}
				}

				if (this.scopeAlive) {
					setTimeout(updateCounter.bind(this), timeoutInterval);
				}
			}.bind(this)());
		},
		getStorageused() {
			let totalBytes = this.videoFiles.reduce((tf, item) => tf + item.bytes, 0);
			const k = 1000;
			const decimals = 2;
			const dm = decimals < 0 ? 0 : decimals;
			const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
			const i = Math.floor(Math.log(totalBytes) / Math.log(k));
			totalBytes = parseFloat((totalBytes / Math.pow(k, i)).toFixed(dm));
			return {
				totalBytes: totalBytes,
				unit: sizes[i]
			}
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Layout driven by container queries — the stats column width does not
   track the viewport. Narrow: Viewers | Bitrate on row 1, Quality
   full-width on row 2. Wide: one weighted row. */
.stats-container {
	container-type: inline-size;
}

.media-info {
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.media-info > .stat-quality {
	grid-column: 1 / -1;
}

.media-info > :nth-child(even) {
	border-left: 1px solid var(--surface-3);
}

.media-info > :nth-child(n+3) {
	border-top: 1px solid var(--surface-3);
}

@container (min-width: 560px) {
	.media-info {
		/* Format hugs its content (max-content floor stops clipping at the
		   560px threshold); its former slack goes to Viewers/Bitrate */
		grid-template-columns: 0.8fr 1.35fr minmax(max-content, 1.3fr);
	}

	.media-info > .stat-quality {
		grid-column: auto;
	}

	.media-info > :nth-child(n+2) {
		border-left: 1px solid var(--surface-3);
		border-top: 0;
	}
}

@container (min-width: 800px) {
	.media-info > * {
		padding-left: 16px;
		padding-right: 16px;
	}
}
</style>
